import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, updateDoc, arrayUnion, arrayRemove, getDoc } from 'firebase/firestore';
import { Plus, X, Tag, Lock } from 'lucide-react';
import { useSubscriptionLimits } from '../hooks/useSubscription';
import { Link } from 'react-router-dom';

interface CustomCategoriesManagerProps {
  householdId: string;
}

export function CustomCategoriesManager({ householdId }: CustomCategoriesManagerProps) {
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [newCategory, setNewCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const subscription = useSubscriptionLimits();

  useEffect(() => {
    loadCategories();
  }, [householdId]);

  async function loadCategories() {
    try {
      const householdRef = doc(db, 'households', householdId);
      const householdDoc = await getDoc(householdRef);

      if (householdDoc.exists()) {
        const data = householdDoc.data();
        setCustomCategories(data.customCategories || []);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load categories');
      setLoading(false);
    }
  }

  async function handleAddCategory() {
    if (!newCategory.trim()) return;

    // Check if at limit
    if (customCategories.length >= subscription.maxCustomCategories) {
      setError(`Free users can create up to ${subscription.maxCustomCategories} custom categories. Upgrade to Premium for unlimited!`);
      return;
    }

    // Check for duplicates
    if (customCategories.includes(newCategory.trim())) {
      setError('Category already exists');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const householdRef = doc(db, 'households', householdId);
      await updateDoc(householdRef, {
        customCategories: arrayUnion(newCategory.trim())
      });

      setCustomCategories([...customCategories, newCategory.trim()]);
      setNewCategory('');
    } catch (err) {
      console.error('Error adding category:', err);
      setError('Failed to add category');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemoveCategory(category: string) {
    if (!window.confirm(`Remove category "${category}"? Items with this category will still show it, but you won't be able to add new items to it.`)) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      const householdRef = doc(db, 'households', householdId);
      await updateDoc(householdRef, {
        customCategories: arrayRemove(category)
      });

      setCustomCategories(customCategories.filter(c => c !== category));
    } catch (err) {
      console.error('Error removing category:', err);
      setError('Failed to remove category');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <p className="text-gray-500">Loading categories...</p>
      </div>
    );
  }

  const canAddMore = customCategories.length < subscription.maxCustomCategories;

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
      <div className="flex items-center gap-3 mb-4">
        <Tag className="w-6 h-6 text-primary-green" />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-bold text-gray-900">Custom Categories</h3>
            <span className="bg-primary-green text-white px-3 py-1 rounded-full text-sm font-semibold">
              {customCategories.length}/{subscription.maxCustomCategories === Infinity ? '∞' : subscription.maxCustomCategories}
            </span>
          </div>
          <p className="text-sm text-gray-600">
            Create categories beyond Fridge, Freezer, and Pantry
          </p>
        </div>
      </div>

      {/* Add New Category */}
      <div className="mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
            placeholder="e.g., Kid's Snacks, Meal Prep, Leftovers..."
            disabled={!canAddMore || saving}
            className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-primary-green transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <button
            onClick={handleAddCategory}
            disabled={!canAddMore || saving || !newCategory.trim()}
            className="px-3 py-2 bg-primary-green text-white rounded-lg font-semibold hover:bg-primary-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1.5 whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>

        {!canAddMore && !subscription.isActive && (
          <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Lock className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-yellow-800 font-semibold">
                  You've reached the free limit of {subscription.maxCustomCategories} custom categories
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Upgrade to Premium for unlimited custom categories!
                </p>
                <Link
                  to="/pricing"
                  className="inline-block mt-2 text-xs font-semibold text-yellow-600 hover:text-yellow-700 underline"
                >
                  View Premium Plans →
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Current Categories */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Your Custom Categories</h4>
        {customCategories.length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            No custom categories yet. Add one above to get started!
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {customCategories.map((category) => (
              <div
                key={category}
                className="flex items-center gap-2 bg-primary-mint/30 border border-primary-green/30 rounded-lg px-3 py-2"
              >
                <Tag className="w-4 h-4 text-primary-green" />
                <span className="text-sm font-medium text-gray-900">{category}</span>
                <button
                  onClick={() => handleRemoveCategory(category)}
                  disabled={saving}
                  className="ml-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                  title="Remove category"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-xs text-blue-900">
          <strong>💡 Tip:</strong> Custom categories help you organize items beyond basic storage locations.
          Try "Meal Prep", "Kid's Lunch", "Leftovers", "Baking Supplies", or "Work Lunch"!
        </p>
      </div>
    </div>
  );
}
